import React from 'react';
import { Filter, Calendar } from 'lucide-react';

export default function FilterControls({ filters, onFiltersChange }) {
  const actionTypes = [
    'role_created', 'role_updated', 'role_deleted', 'role_assigned', 'role_unassigned',
    'permission_granted', 'permission_revoked', 'permission_updated',
    'security_setting_updated', 'rls_policy_check', 'database_sync', 'system_error'
  ];

  const severityLevels = ['critical', 'high', 'medium', 'low'];

  const handleActionTypeToggle = (actionType) => {
    const current = filters?.actionType || [];
    const updated = current?.includes(actionType)
      ? current?.filter(t => t !== actionType)
      : [...current, actionType];
    onFiltersChange({ ...filters, actionType: updated });
  };

  const handleSeverityToggle = (severity) => {
    const current = filters?.severity || [];
    const updated = current?.includes(severity)
      ? current?.filter(s => s !== severity)
      : [...current, severity];
    onFiltersChange({ ...filters, severity: updated });
  };

  const handleDateChange = (field, value) => {
    onFiltersChange({ ...filters, [field]: value });
  };

  const handleDecisionChange = (decision) => {
    onFiltersChange({ ...filters, decision: filters?.decision === decision ? null : decision });
  };

  const clearFilters = () => {
    onFiltersChange({
      startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString(),
      endDate: new Date()?.toISOString(),
      actionType: [],
      severity: [],
      decision: null
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Filter className="w-5 h-5 mr-2" />
          Filter Controls
        </h3>
        <button
          onClick={clearFilters}
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          Clear All
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            Date Range
          </label>
          <div className="space-y-2">
            <input
              type="date"
              value={filters?.startDate?.split('T')?.[0] || ''}
              onChange={(e) => handleDateChange('startDate', new Date(e.target.value)?.toISOString())}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <input
              type="date"
              value={filters?.endDate?.split('T')?.[0] || ''}
              onChange={(e) => handleDateChange('endDate', new Date(e.target.value)?.toISOString())}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* Action Types */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Action Types
          </label>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {actionTypes?.map((type) => (
              <label key={type} className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={filters?.actionType?.includes(type)}
                  onChange={() => handleActionTypeToggle(type)}
                  className="rounded border-gray-300"
                />
                <span className="text-gray-700">{type?.replace(/_/g, ' ')}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Severity Levels */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Severity Levels
          </label>
          <div className="space-y-1">
            {severityLevels?.map((severity) => (
              <label key={severity} className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={filters?.severity?.includes(severity)}
                  onChange={() => handleSeverityToggle(severity)}
                  className="rounded border-gray-300"
                />
                <span className="text-gray-700 capitalize">{severity}</span>
              </label>
            ))}
          </div>
        </div>

        {/* RLS Decision */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            RLS Policy Decision
          </label>
          <div className="space-y-1">
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="radio"
                checked={filters?.decision === 'allowed'}
                onChange={() => handleDecisionChange('allowed')}
                className="border-gray-300"
              />
              <span className="text-gray-700">Allowed</span>
            </label>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="radio"
                checked={filters?.decision === 'denied'}
                onChange={() => handleDecisionChange('denied')}
                className="border-gray-300"
              />
              <span className="text-gray-700">Denied</span>
            </label>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="radio"
                checked={filters?.decision === null}
                onChange={() => handleDecisionChange(null)}
                className="border-gray-300"
              />
              <span className="text-gray-700">All</span>
            </label>
          </div>
        </div>
      </div>
      {/* Active Filters Summary */}
      {(filters?.actionType?.length > 0 || filters?.severity?.length > 0 || filters?.decision) && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Active filters: 
            {filters?.actionType?.length > 0 && ` ${filters?.actionType?.length} action types`}
            {filters?.severity?.length > 0 && ` ${filters?.severity?.length} severity levels`}
            {filters?.decision && ` RLS: ${filters?.decision}`}
          </p>
        </div>
      )}
    </div>
  );
}